import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@refinedev/core';
import { Flex, Select, TextInput } from '@mantine/core';
import { Payor } from 'interfaces';
import {
  CUSTOM_FAX_RECIPIENT,
  buildFaxRecipientMap,
  getClaimPayorFaxRecipient,
  normalizeFaxNumber,
} from 'utils/faxUtils';
import { validatePhoneNumber } from 'utils/validate';

export interface ClaimFaxRecipientSelection {
  isValid: boolean;
  recipientValue: string;
}

interface ClaimFaxRecipientSelectorProps {
  payors: Payor[];
  claimPayorID?: string | null;
  disabled?: boolean;
  onChange?: (selection: ClaimFaxRecipientSelection) => void;
}

export const ClaimFaxRecipientSelector = ({
  payors = [],
  claimPayorID,
  disabled = false,
  onChange,
}: ClaimFaxRecipientSelectorProps) => {
  const { translate } = useTranslation();

  const faxRecipientByValue = useMemo(
    () => buildFaxRecipientMap(payors),
    [payors]
  );

  const claimPayorFax = useMemo(
    () => getClaimPayorFaxRecipient(payors, claimPayorID),
    [claimPayorID, payors]
  );

  const defaultRecipient = useMemo(
    () => claimPayorFax?.id || CUSTOM_FAX_RECIPIENT,
    [claimPayorFax?.id]
  );

  const [selectedRecipient, setSelectedRecipient] = useState<string | null>(
    defaultRecipient
  );
  const [customFaxNumber, setCustomFaxNumber] = useState('');

  useEffect(() => {
    setSelectedRecipient(defaultRecipient);
    setCustomFaxNumber('');
  }, [defaultRecipient]);

  const recipientValue =
    selectedRecipient === CUSTOM_FAX_RECIPIENT
      ? normalizeFaxNumber(customFaxNumber)
      : faxRecipientByValue.get(selectedRecipient || '') || '';

  const hasValidRecipient = validatePhoneNumber(recipientValue);

  useEffect(() => {
    onChange?.({
      isValid: hasValidRecipient,
      recipientValue,
    });
  }, [hasValidRecipient, onChange, recipientValue]);

  const faxRecipientOptions = useMemo(
    () => [
      ...payors.map((payor) => ({
        value: payor.id,
        label: `${payor.name} (${payor.fax})`,
      })),
      {
        value: CUSTOM_FAX_RECIPIENT,
        label: translate('pages:claims.claimInvoices.customFaxNumberOption'),
      },
    ],
    [payors, translate]
  );

  const isCustomRecipient = selectedRecipient === CUSTOM_FAX_RECIPIENT;

  return (
    <Flex direction="column" gap="sm">
      <Select
        label={translate('pages:claims.claimInvoices.faxRecipient')}
        placeholder={translate('pages:claims.claimInvoices.selectFaxRecipient')}
        data={faxRecipientOptions}
        value={selectedRecipient}
        allowDeselect={false}
        ml={10}
        w="30%"
        disabled={disabled}
        onChange={(value) => {
          setSelectedRecipient(value);
          if (value !== CUSTOM_FAX_RECIPIENT) {
            setCustomFaxNumber('');
          }
        }}
        data-testid={'fax-recipient-select'}
      />
      {isCustomRecipient && (
        <TextInput
          label={translate('pages:claims.claimInvoices.customFaxNumber')}
          placeholder="+12045550123"
          value={customFaxNumber}
          disabled={disabled}
          w="30%"
          ml={10}
          onChange={(event) => {
            setCustomFaxNumber(event.currentTarget.value);
          }}
          data-testid={'custom-fax-number-input'}
        />
      )}
    </Flex>
  );
};
